/**
 * Safe post-login redirect target.
 * Telegram Mini Apps put launch params in the URL hash (`#tgWebAppData=...`).
 * Vue Router's fullPath includes that hash — stuffing it into `returnTo` breaks
 * Welcome/auth redirects and looks like a frozen app (EmptyLayout, no nav).
 */

const AUTH_PATHS = new Set(['/welcome', '/login', '/register', '/desktop/auth'])

function isTelegramLaunchNoise(value) {
  return typeof value === 'string' && /tgWebApp/i.test(value)
}

/**
 * @param {import('vue-router').RouteLocationNormalizedLoaded | import('vue-router').RouteLocationNormalized} route
 * @returns {string | undefined} encodeURIComponent'd path[+query], or undefined to omit
 */
export function buildReturnTo(route) {
  if (!route) {
    return undefined
  }

  // Launch params live in hash — never persist them
  if (isTelegramLaunchNoise(route.hash)) {
    if (!route.path || route.path === '/') {
      return undefined
    }
  }

  const path = route.path || '/'
  if (AUTH_PATHS.has(path)) {
    return undefined
  }

  const params = new URLSearchParams()
  const query = route.query || {}

  Object.keys(query).forEach((key) => {
    if (key === 'returnTo' || key.startsWith('tgWebApp')) {
      return
    }
    const value = query[key]
    if (value == null) {
      return
    }
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item != null && !isTelegramLaunchNoise(String(item))) {
          params.append(key, String(item))
        }
      })
      return
    }
    if (!isTelegramLaunchNoise(String(value))) {
      params.set(key, String(value))
    }
  })

  const qs = params.toString()
  const target = qs ? `${path}?${qs}` : path

  if (target === '/' || target === '') {
    return undefined
  }

  return encodeURIComponent(target)
}

/**
 * @param {unknown} raw
 * @returns {{ path: string, query: Record<string, string> } | null}
 */
export function parseReturnTo(raw) {
  if (raw == null || raw === '') {
    return null
  }

  let decoded = String(raw)
  for (let i = 0; i < 3; i++) {
    try {
      const next = decodeURIComponent(decoded)
      if (next === decoded) {
        break
      }
      decoded = next
      if (!/%[0-9A-Fa-f]{2}/.test(decoded)) {
        break
      }
    } catch {
      break
    }
  }

  if (isTelegramLaunchNoise(decoded)) {
    return null
  }

  try {
    const url = new URL(decoded, window.location.origin)
    if (isTelegramLaunchNoise(url.hash) && (url.pathname === '/' || url.pathname === '')) {
      return null
    }
    if (AUTH_PATHS.has(url.pathname)) {
      return null
    }

    const query = {}
    url.searchParams.forEach((value, key) => {
      if (key === 'returnTo' || key.startsWith('tgWebApp')) {
        return
      }
      query[key] = value
    })

    return {
      path: url.pathname || '/',
      query,
    }
  } catch {
    return null
  }
}
